import { useCallback, useState } from 'react';

export interface UseRecommendationDialogParams<TResult> {
  fetchFn: (prompt: string) => Promise<TResult[]>;
  applyFn: (results: TResult[]) => Promise<void>;

  onFetchSucceeded?: (results: TResult[]) => void;
  onFetchFailed?: (error: any) => void;
  onApplySucceeded?: (res?: any) => void;
  onApplyFailed?: (error: any) => void;
}

export const useRecommendationDialog = <TResult>(params: UseRecommendationDialogParams<TResult>) => {
  const {
    fetchFn,
    applyFn,
    onFetchSucceeded,
    onFetchFailed,
    onApplySucceeded,
    onApplyFailed,
  } = params;

  const [prompt, setPrompt] = useState<string>('');
  const [results, setResults] = useState<TResult[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isApplying, setIsApplying] = useState<boolean>(false);

  const reset = useCallback(() => {
    setPrompt('');
    setResults([]);
    setErrorMessage(undefined);
    setIsLoading(false);
    setIsApplying(false);
  }, []);

  const fetch = useCallback((promptToFetch: string) => {
    setIsLoading(true);
    setResults([]);
    setErrorMessage(undefined);

    fetchFn(promptToFetch)
      .then((fetchedResults) => {
        setResults(fetchedResults);
        onFetchSucceeded?.(fetchedResults);
      })
      .catch((error) => {
        setErrorMessage(error.message);
        // istanbul ignore next
        onFetchFailed?.(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [fetchFn, onFetchSucceeded, onFetchFailed]);

  const apply = useCallback(() => {
    setIsApplying(true);

    applyFn(results)
      .then((res) => {
        reset();
        onApplySucceeded?.(res);
      })
      .catch((error) => {
        setErrorMessage(error.message);
        // istanbul ignore next
        onApplyFailed?.(error);
      })
      .finally(() => {
        setIsApplying(false);
      });
  }, [applyFn, results, reset, onApplySucceeded, onApplyFailed]);

  return {
    prompt,
    results,
    errorMessage,
    isLoading,
    isApplying,
    reset,
    fetch,
    apply,
    updatePrompt: setPrompt,
    updateResults: setResults,
  };
};
